import { useNavigate } from "react-router-dom";
import {
    useTheme,
    useMediaQuery,
    Box,
    Button,
    Badge,
} from '@mui/material';
import { AddLinkRounded, TagRounded } from '@mui/icons-material';
import { usePather } from 'react-pather';
import ModeToggle, { Mode } from './ModeToggle';
import FreshnessToggle, { Freshness } from './FreshnessToggle';
import PerPageSwitcher from './PerPageSwitcher';
import SortByDate from './SortByDate';
import FilterByDate from './FilterByDate';
import { SearchInput, ExpansionToggle } from '../../../../general';

const ConversionListSettings = ({ 
    queryLoading, 
    numberOfResults, 
    isExpanded,
    setExpansion,
    setPerPage,
    settings, 
    setMethods,
    setOpenTagsSelectModal, 
    pickedFilteringTagCodesAmount,
    withViewSwitcher = true 
}) => {

    const {
        query,
        mode, 
        freshness, 
        perPage, 
        dateSort
    } = settings;

    const { 
        setQuery,
        setMode,
        setFreshness,
        setStartDate,
        setEndDate,
        setDateSort,
    } = setMethods;

    const theme = useTheme();
    const matchesDown400px = useMediaQuery(theme.breakpoints.down(400));
    const matchesSm = useMediaQuery(theme.breakpoints.only('sm'));
    
    return (
        <Box 
            sx={{
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
            }}
        >
            <Box 
                sx={{
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: 2,
                }}
            >
                {/* TODO: try to display SearchInput normally without "sxWrapper" */}
                <Box sx={{ width: { xs: '100%', sm: '70%' } }}>
                    <SearchInput 
                        query={query}
                        onChange={e => setQuery(e.target.value)}
                        onClear={() => setQuery("")}
                        loading={queryLoading}
                        sxWrapper={{
                            width: "100%",
                        }}
                    />
                </Box>
                <ShortenButton/>
            </Box>
            <Box className="container-card" sx={{mt: 1}}>
                <Box className="square" sx={{
                    height: '100%', 
                    width: '100%',
                    px: 3, 
                    pt: 1.9, 
                    pb: 1.5,
                    boxShadow: theme.palette.squareBoxShadow,
                    zIndex: 1,
                    borderRadius: '50px',
                    border: theme.palette.tinyBorder,
                }}>
                    <Box sx={{
                        width: '100%', 
                        display: 'flex',
                        alignItems: {
                            xs: 'flex-start', 
                            sm: 'center',
                        }, 
                        justifyContent: 'center',
                        flexDirection: {
                            xs: 'column', 
                            sm: 'row',
                        }, 
                        gap: {sm: 2},
                    }}>
                        <Box sx={{minWidth: '100px', mb: {xs: 1.5, sm: 0}}}>
                            <span>Results: <b>{numberOfResults}</b></span>
                        </Box>
                        <Box sx={{
                            width: {sm: '100%'}, 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: {md: 'center'},
                            alignItems: {xs: 'flex-start', md: 'center'},
                            flexDirection: {xs: matchesDown400px || matchesSm ? 'column' : 'row', md: 'row'}
                        }}>
                            {
                                withViewSwitcher &&
                                <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1}}>
                                    <ModeToggle 
                                        mode={mode}
                                        setMode={setMode}
                                    />
                                    <FreshnessToggle
                                        freshness={freshness}
                                        setFreshness={setFreshness}
                                    />
                                </Box>
                            }
                            <FilterByDate 
                                setStartDate={date => setStartDate(date)}
                                setEndDate={date => setEndDate(date)}
                                settings={settings}
                            />
                            <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1}}>
                                <Badge badgeContent={pickedFilteringTagCodesAmount} color="primary">
                                    <Box
                                        sx={{
                                            p: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            border: theme.palette.tinyBorder,
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            "&:hover": {
                                                border: `1px solid ${theme.palette.mode === "dark" ? "white" : "black"}`,
                                            }
                                        }}
                                        onClick={() => setOpenTagsSelectModal(true)}
                                    >
                                        <TagRounded/>
                                    </Box>
                                </Badge>
                                <ExpansionToggle 
                                    isExpanded={isExpanded}
                                    setExpansion={setExpansion}
                                />
                                <PerPageSwitcher 
                                    perPage={perPage}
                                    setPerPage={setPerPage}
                                />
                            </Box>
                        </Box>
                        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <SortByDate 
                                dateSort={dateSort} 
                                onChange={() => setDateSort(dateSort === 'down' ? 'up' : 'down')}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

const ShortenButton = () => {
    const pather = usePather();
    const navigate = useNavigate();
    return (
        <Button 
            variant="contained"
            color="primary"
            sx={{ 
                minWidth: '56px',
                height: '56px',  
                borderRadius: '50%',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3,
            }}
            onClick={() => navigate(pather.front.shorten)}
        >
            <AddLinkRounded fontSize="medium" />
        </Button>
    );
}

export default ConversionListSettings;

export {
    Mode,
    Freshness,
    PerPageSwitcher,
    FilterByDate,
    SortByDate,
}